// Page header for ProjectDetail: title + drawing number on the left,
// Export Excel / Export PDF / Re-run extraction / Back nav on the
// right. Export buttons only render when the project has reached
// completed status. Re-run is available for completed or failed —
// pending / preprocessing / extracting / validating projects already
// have a run in flight, so re-triggering would 409.
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { api, apiDownload } from "../../../lib/api";
import type { TakeoffProjectDto } from "../../../../../shared/types/pipe-takeoff";

interface Props {
  project: TakeoffProjectDto | null;
  projectId: number;
}

export function ProjectHeader({ project, projectId }: Props) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fallbackName = project?.name || "takeoff";
  const canRerun =
    project?.status === "completed" || project?.status === "failed";

  const rerun = async () => {
    if (
      !window.confirm(
        "Re-run extraction? This replaces the current items with the new run's output. Manual edits will be lost.",
      )
    ) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/api/tools/pipe-takeoff/projects/${projectId}/extract`, {
        method: "POST",
      });
      navigate(`/tools/pipe-takeoff/${projectId}/extract`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  const deleteProject = async () => {
    const name = project?.name || "this project";
    if (
      !window.confirm(
        `Delete "${name}"? This removes the project, items, and any uploaded PDF. Cannot be undone.`,
      )
    ) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/api/tools/pipe-takeoff/projects/${projectId}`, {
        method: "DELETE",
      });
      navigate("/tools/pipe-takeoff");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div className="bsp-page-header" style={{ justifyContent: "space-between" }}>
      <div>
        <h1 className="bsp-page-title">{project?.name ?? "Project"}</h1>
        {project?.drawingNumber && (
          <div className="bsp-mono" style={{ color: "var(--bsp-fg-muted)", fontSize: 12 }}>
            {project.drawingNumber}
          </div>
        )}
        {error && (
          <div style={{ color: "var(--bsp-red-fg)", fontSize: 12, marginTop: 4 }}>
            {error}
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        {project?.status === "completed" && (
          <>
            <button
              type="button"
              className="bsp-btn bsp-btn-secondary"
              onClick={() =>
                void apiDownload(
                  `/api/tools/pipe-takeoff/projects/${projectId}/export.xlsx`,
                  `${fallbackName}.xlsx`,
                )
              }
            >
              Export Excel
            </button>
            <button
              type="button"
              className="bsp-btn bsp-btn-secondary"
              onClick={() =>
                void apiDownload(
                  `/api/tools/pipe-takeoff/projects/${projectId}/export.pdf`,
                  `${fallbackName}.pdf`,
                )
              }
            >
              Export PDF
            </button>
          </>
        )}
        {canRerun && (
          <button
            type="button"
            className="bsp-btn bsp-btn-primary"
            onClick={() => void rerun()}
            disabled={busy}
            title="Discard current items and run extraction again"
          >
            {busy ? "Starting…" : "Re-run extraction"}
          </button>
        )}
        <Link to="/tools/pipe-takeoff" className="bsp-btn bsp-btn-secondary">
          Back to projects
        </Link>
        <button
          type="button"
          className="bsp-btn bsp-btn-danger"
          onClick={() => void deleteProject()}
          disabled={busy}
          title="Delete this project, items and PDF"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

